import { useState, useEffect, memo } from 'react';
import { Bot, Shield } from 'lucide-react';
import { usersApiService } from '../services/usersApi';
import { PlayerProfile } from './PlayerProfile';
import { useUsername } from '../services/usernameCache';
import { isAutoModeration, isStewardBot } from '../utils/automod';

interface CreatorProfileProps {
  creatorId: string;
  inline?: boolean;
}

const discordSuffixes = ['@legacy-discord.playcdu.co', '@discord.playcdu.internal'];

function extractDiscordId(creatorId: string): string | null {
  for (const suffix of discordSuffixes) {
    if (creatorId.endsWith(suffix)) {
      return creatorId.slice(0, -suffix.length);
    }
  }
  return null;
}

export const CreatorProfile = memo(({ creatorId, inline = false }: CreatorProfileProps) => {
  if (!creatorId) {
    return <span className="text-glass-400">Unknown</span>;
  }

  if (isAutoModeration(creatorId)) {
    if (inline) {
      return (
        <span className="inline-flex items-center gap-1 font-medium text-amber-400 align-middle">
          <Shield className="w-3 h-3" />
          AutoMod
        </span>
      );
    }
    return (
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="icon-tile icon-tile-amber w-8 h-8 rounded-md">
          <Shield className="w-4 h-4" />
        </span>
        <span className="font-medium text-amber-400">AutoMod</span>
      </div>
    );
  }

  // The steward bot writes Staff Manager Notes itself (automatic ticket-MVP commendations).
  if (isStewardBot(creatorId)) {
    if (inline) {
      return (
        <span className="inline-flex items-center gap-1 font-medium text-accent-300 align-middle" title="Applied automatically by the steward bot">
          <Bot className="w-3 h-3" />
          Steward bot
        </span>
      );
    }
    return (
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="icon-tile icon-tile-accent w-8 h-8 rounded-md">
          <Bot className="w-4 h-4" />
        </span>
        <span className="font-medium text-accent-300">Steward bot</span>
      </div>
    );
  }

  const [uuid, setUuid] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDiscordLookup, setIsDiscordLookup] = useState(false);

  const isUuidFormat = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(creatorId);
  const suffixedDiscordId = extractDiscordId(creatorId);
  const isRawDiscordId = !isUuidFormat && /^\d{17,20}$/.test(creatorId);
  const isEmail = creatorId.includes('@');
  const isPlainUsername = !isUuidFormat && !suffixedDiscordId && !isRawDiscordId && !isEmail && /^[a-zA-Z0-9_]{2,16}$/.test(creatorId);

  const cachedUsername = useUsername(isUuidFormat ? creatorId : '');

  useEffect(() => {
    setUuid(null);
    setUsername(null);
    setError(null);
    setIsDiscordLookup(false);

    if (isUuidFormat) {
      setUuid(creatorId);
      setUsername(cachedUsername);
    } else if (suffixedDiscordId !== null || isRawDiscordId) {
      setIsDiscordLookup(true);
      const discordId = suffixedDiscordId ?? creatorId;

      if (/^\d+$/.test(discordId)) {
        usersApiService.getPlayerStatsByDiscordId(discordId)
          .then(response => {
            if (response.player_stats) {
              setUuid(response.player_stats.uuid);
              setUsername(response.player_stats.username);
            } else {
              setError('not_found');
            }
          })
          .catch(() => setError('fetch_failed'));
      }
    } else if (isPlainUsername) {
      usersApiService.getPlayerStatsByUsername(creatorId)
        .then(response => {
          if (response.player_stats) {
            setUuid(response.player_stats.uuid);
            setUsername(response.player_stats.username);
          }
        })
        .catch(() => {});
    }
  }, [creatorId, cachedUsername, isUuidFormat, suffixedDiscordId, isRawDiscordId, isPlainUsername]);

  if (uuid) {
    return <PlayerProfile uuid={uuid} name={username || undefined} inline={inline} />;
  }

  if (isDiscordLookup) {
    if (error) {
      const discordId = suffixedDiscordId ?? creatorId;
      if (inline) {
        return <span className="font-medium text-glass-300" title={`Discord ID: ${discordId}`}>Discord User</span>;
      }
      return (
        <div className="flex items-center gap-2">
          <span className="font-medium text-glass-300" title={`Discord ID: ${discordId}`}>Discord User</span>
        </div>
      );
    }
    return <span className="skeleton inline-block h-3.5 w-24 align-middle" title="Loading creator…" aria-label="Loading creator" />;
  }

  const displayName = isUuidFormat ? (cachedUsername || creatorId) : creatorId;

  if (inline) {
    return <span className="font-medium text-white">{displayName}</span>;
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="font-medium text-white truncate">{displayName}</span>
    </div>
  );
});
